import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { graphql } from "react-relay";

import { faEdit, faTimes } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import {
	faArrowRight,
	faChevronDown,
	faChevronUp,
	faEllipsisV,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import moment from "moment";

import AppContext from "~/common/AppContext";
import { transformCustomEmojiFromHTML } from "~/common/Emoji";
import { commitMutationPromise } from "~/lib/relayUtil";
import type { NodesFromEdges } from "~/lib/utils";
import { environment } from "~/models";
import type { ObjectiveAttributes } from "~/okrs/Objective";
import UpdateDescription from "~/okrs/Updates/UpdateDescription";
import Button from "~/ui/button/Button";
import IconButton from "~/ui/button/IconButton";
import { LabelIconForInlineButton } from "~/ui/button/InlineButton";
import Avatar from "~/ui/legacy/Avatar";
import Menu from "~/ui/menu";
import toast from "~/ui/toast";

import type { UpdateChangeDescriptionMutation } from "~/__generated__/UpdateChangeDescriptionMutation.graphql";
import type { UpdatesViewer$data as UpdatesViewer } from "~/__generated__/UpdatesViewer.graphql";

import styles from "./Update.module.css";

type Update = NodesFromEdges<UpdatesViewer["updates"]>;

interface UpdateProps {
	update: Update;
	hasAccess?: boolean;
	contributors: ObjectiveAttributes["contributors"];
}

const dateFormats = {
	lastDay: "[Yesterday]",
	lastWeek: "[Last] dddd",
	sameElse: "MMM D, YYYY",
};

const StatusColours: { [key: string]: string } = {
	"No status": styles.statusGrey,
	"On track": styles.statusGreen,
	"At risk": styles.statusRed,
	Behind: styles.statusRed,
	Completed: styles.statusGreen,
	Abandoned: styles.statusGrey,
};

const changeDescriptionMutation = graphql`
	mutation UpdateChangeDescriptionMutation($updateId: ID!, $text: String!, $json: JSON!) @raw_response_type {
		Objective_EditUpdateDescription(updateId: $updateId, text: $text, json: $json) {
			update {
				id
				data
			}
		}
	}
`;

export default function Update({ update, hasAccess, contributors }: UpdateProps) {
	const [isEditingDescription, setIsEditingDescription] = useState<boolean>(false);
	const [isDescriptionExpanded, setIsDescriptionExpanded] = useState<boolean>(false);
	const [canExpand, setCanExpand] = useState<boolean>(false);
	const { user } = useContext(AppContext);
	const descriptionRef = useRef<HTMLParagraphElement>(null);

	const { id, __typename, createdAt, cache, data, verb, assignee, assigneeTeam, keyResult, objective } = update;

	const { text: description, html } = data;
	const actor = update.actor || cache.actor;
	const isUserActor = user?.id === actor.id;

	useEffect(() => {
		if (!descriptionRef.current) {
			return;
		}
		const overflowed = descriptionRef.current?.scrollHeight > 60;
		setCanExpand(overflowed);
	}, [descriptionRef, description, setCanExpand]);

	const formattedDate = useMemo((): React.ReactNode => {
		const now = moment();
		const momentDate = moment(createdAt);
		if (!momentDate.isSame(now, "day")) {
			return momentDate.calendar(undefined, dateFormats);
		}
		const hoursDifference = now.diff(momentDate, "hours");
		if (hoursDifference > 0) {
			return `${hoursDifference}h ago`;
		}
		const minutesDifference = now.diff(momentDate, "minutes");
		return minutesDifference > 0 ? `${minutesDifference}m ago` : "Just now";
	}, [createdAt]);

	const content = useMemo((): React.ReactNode => {
		let title: React.ReactNode;
		let subtitle: React.ReactNode;
		switch (verb) {
			case "OBJECTIVE_CREATED":
				title = "Objective created";
				break;

			case "OBJECTIVE_ASSIGNED":
				title = (
					<>
						Objective assigned to <strong>{assignee ? assignee.fullName : ""}</strong>
					</>
				);
				break;

			case "KEY_RESULT_ASSIGNED": {
				const keyResultTitle = keyResult ? keyResult.title || "Untitled key result" : "Deleted key result";
				title = (
					<>
						Key result <strong>{keyResultTitle}</strong> assigned to{" "}
						<strong>{assignee ? assignee.fullName : ""}</strong>
					</>
				);
				break;
			}

			case "OBJECTIVE_ASSIGNED_TEAM":
				title = (
					<>
						Objective assigned to <strong>{assigneeTeam ? assigneeTeam.title : ""}</strong>
					</>
				);
				break;

			case "KEY_RESULT_ASSIGNED_TEAM": {
				const keyResultTitle = keyResult ? keyResult.title || "Untitled key result" : "Deleted key result";
				title = (
					<>
						Key result <strong>{keyResultTitle}</strong> assigned to{" "}
						<strong>{assigneeTeam ? assigneeTeam.title : ""}</strong>
					</>
				);
				break;
			}

			case "OBJECTIVE_PROGRESS":
				title = "Objective progress updated:";
				subtitle = (
					<>
						{data.old}% <FontAwesomeIcon icon={faArrowRight} /> {data.new}%
					</>
				);
				break;

			case "OBJECTIVE_STATUS_CHANGE": {
				title = "Objective status updated:";
				const oldStatus = data.old;
				const newStatus = data.new;
				subtitle = (
					<>
						<span className={StatusColours[oldStatus]}>{oldStatus}</span>
						<FontAwesomeIcon icon={faArrowRight} /> <span className={StatusColours[newStatus]}>{newStatus}</span>
					</>
				);
				break;
			}

			case "OBJECTIVE_DUE_DATE_CHANGE": {
				if (!data.new && data.old) {
					title = <>Objective due date was removed</>;
					break;
				}

				const formattedDate = moment(data.new).format("MMM D");
				title = (
					<>
						Objective due date set to <strong>{formattedDate}</strong>
					</>
				);
				break;
			}

			case "OBJECTIVE_ARCHIVED":
				title = "Objective archived";
				break;

			case "OBJECTIVE_UPDATED":
				title = `Objective updated ${data.changed ? `the ${data.changed}` : ""}`;
				break;

			case "OBJECTIVE_CREATED_FLOATING_DESCRIPTION": {
				title = objective ? objective.title || "Untitled objective" : "Deleted objective";
				break;
			}

			case "KEY_RESULT_START_VALUE_CHANGE": {
				const keyResultTitle = keyResult ? keyResult.title || "Untitled key result" : "Deleted key result";
				title = (
					<>
						Key result <strong>{keyResultTitle}</strong> updated the start value:
					</>
				);
				subtitle = (
					<>
						{data.old} <FontAwesomeIcon icon={faArrowRight} /> {data.new}
					</>
				);
				break;
			}

			case "KEY_RESULT_CURRENT_VALUE_CHANGE": {
				const keyResultTitle = keyResult ? keyResult.title || "Untitled key result" : "Deleted key result";
				title = (
					<>
						Key result <strong>{keyResultTitle}</strong> updated the current value:
					</>
				);
				subtitle = (
					<>
						{data.old} <FontAwesomeIcon icon={faArrowRight} /> {data.new}
					</>
				);
				break;
			}

			case "KEY_RESULT_TARGET_VALUE_CHANGE": {
				const keyResultTitle = keyResult ? keyResult.title || "Untitled key result" : "Deleted key result";
				title = (
					<>
						Key result <strong>{keyResultTitle}</strong> updated the target value:
					</>
				);
				subtitle = (
					<>
						{data.old} <FontAwesomeIcon icon={faArrowRight} /> {data.new}
					</>
				);
				break;
			}

			case "KEY_RESULT_DUE_DATE_CHANGE": {
				const keyResultTitle = keyResult ? keyResult.title || "Untitled key result" : "Deleted key result";
				const formattedDate = moment(data.new).format("MMM D");
				title = (
					<>
						Key result <strong>{keyResultTitle}</strong> due date set to <strong>{formattedDate}</strong>
					</>
				);
				break;
			}

			case "KEY_RESULT_DELETED": {
				const keyResultTitle = data.title;
				title = (
					<>
						Key result <strong>{keyResultTitle}</strong> was deleted
					</>
				);
				break;
			}

			case "KEY_RESULT_UPDATED": {
				const keyResultTitle = keyResult ? keyResult.title || "Untitled key result" : "Deleted key result";
				title = (
					<>
						Key result <strong>{keyResultTitle}</strong> updated {data.changed ? `the ${data.changed}` : ""}
					</>
				);
				break;
			}

			case "KEY_RESULT_CREATED_FLOATING_DESCRIPTION": {
				title = keyResult ? keyResult.title || "Untitled key result" : "Deleted key result";
				break;
			}
		}
		return (
			<>
				{title && <h1>{title}</h1>}
				{subtitle && <h2>{subtitle}</h2>}
			</>
		);
	}, [verb, assignee, assigneeTeam, data, keyResult, objective]);

	const onStartEditingDescription = useCallback(() => {
		setIsEditingDescription(true);
	}, [setIsEditingDescription]);

	const onStopEditingDescription = useCallback(() => {
		setIsEditingDescription(false);
	}, [setIsEditingDescription]);

	const expandDescription = useCallback(() => {
		setIsDescriptionExpanded(true);
	}, [setIsDescriptionExpanded]);

	const collapseDescription = useCallback(() => {
		setIsDescriptionExpanded(false);
	}, [setIsDescriptionExpanded]);

	const onDescriptionChanged = useCallback(
		(text: string, json: {}, html: string) => {
			onStopEditingDescription();
			commitMutationPromise<UpdateChangeDescriptionMutation>(environment, {
				mutation: changeDescriptionMutation,
				variables: {
					updateId: id,
					text,
					json,
				},
				optimisticResponse: {
					// eslint-disable-next-line camelcase
					Objective_EditUpdateDescription: {
						update: {
							__typename,
							id,
							data: {
								text: text,
								html: html,
							},
						},
					},
				},
				onError: () => {
					toast.error("Failed to update description", { title: "Error" });
				},
			});
		},
		[id, __typename, onStopEditingDescription],
	);

	const commentHTML = React.useMemo(() => {
		return transformCustomEmojiFromHTML(html);
	}, [html]);

	return (
		<article key={id} className={cx(styles.update, { [styles.editing]: isEditingDescription })}>
			<header>
				<Avatar user={actor} width="1.5rem" />
				{actor.fullName}
				<span>{formattedDate}</span>
				{isEditingDescription ? (
					<IconButton
						variant="tertiary"
						title="Close"
						icon={faTimes}
						className={cx(styles.headerButton, styles.closeBtn)}
						onClick={onStopEditingDescription}
						data-test-id="modal-header-close-button"
					/>
				) : isUserActor && hasAccess ? (
					<Menu>
						<Menu.Trigger>
							<IconButton variant="tertiary" title="Menu" icon={faEllipsisV} className={styles.headerButton} />
						</Menu.Trigger>
						<Menu.Content align="end">
							<Menu.Item onSelect={onStartEditingDescription}>
								<LabelIconForInlineButton icon={faEdit} side="left" />
								Edit description
							</Menu.Item>
						</Menu.Content>
					</Menu>
				) : (
					<></>
				)}
			</header>
			{content}

			{isEditingDescription ? (
				<UpdateDescription
					initialDescription={description}
					initialHTML={html}
					onSubmit={onDescriptionChanged}
					className={styles.description}
					contributors={contributors}
				/>
			) : description ? (
				<>
					<div className={styles.comment}>
						<div
							ref={descriptionRef}
							className={cx({ [styles.collapsed]: !isDescriptionExpanded }, "ProseMirror")}
							dangerouslySetInnerHTML={{ __html: commentHTML }}
						/>
					</div>

					{canExpand &&
						(isDescriptionExpanded ? (
							<Button size="normal" className={styles.collapser} variant="tertiary" onClick={collapseDescription}>
								Show less
								<FontAwesomeIcon icon={faChevronUp} />
							</Button>
						) : (
							<Button size="normal" className={styles.collapser} variant="tertiary" onClick={expandDescription}>
								Show more
								<FontAwesomeIcon icon={faChevronDown} />
							</Button>
						))}
				</>
			) : (
				isUserActor &&
				hasAccess && (
					<Button size="normal" variant="tertiary" onClick={onStartEditingDescription}>
						Add a comment
					</Button>
				)
			)}
		</article>
	);
}
